/**
 * Blocs de code des articles (#22266).
 *
 * Habille chaque bloc `core/code` d'une barre de titre — le langage à gauche,
 * un bouton de copie à droite — et confie la coloration à Prism. Le langage
 * vient de la classe `language-<slug>` posée sur le <pre> par le sélecteur de
 * l'éditeur (assets/js/editor/code-language.js) ; un bloc sans langage garde sa
 * boîte et son bouton, simplement sans couleurs.
 *
 * Ce bundle est chargé par includes/theme.php uniquement sur les contenus qui
 * contiennent un bloc code : inutile de servir Prism au reste du site.
 */
import './prism-manual';
import Prism from 'prismjs';

// Langages retenus pour les articles Gladhost (infra, hébergement, WordPress).
// `markup`, `css`, `clike` et `javascript` sont déjà dans le cœur de Prism.
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup-templating'; // requis par php
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-nginx';
import 'prismjs/components/prism-apacheconf';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-ini';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-diff';

/**
 * Étiquettes affichées dans la barre. Le slug Prism ne fait pas toujours un
 * libellé présentable (`apacheconf`, `docker`), d'où cette table ; un langage
 * absent d'ici s'affiche en capitales.
 */
const ETIQUETTES = {
    bash: 'Bash',
    yaml: 'YAML',
    json: 'JSON',
    php: 'PHP',
    nginx: 'Nginx',
    apacheconf: 'Apache',
    sql: 'SQL',
    ini: 'INI',
    docker: 'Dockerfile',
    diff: 'Diff',
    markup: 'HTML',
    css: 'CSS',
    javascript: 'JavaScript',
};

const SELECTEUR = 'pre.wp-block-code';
const CLASSE_LANGAGE = /(?:^|\s)language-([\w-]+)/;

/**
 * Lit le langage déclaré sur le bloc.
 *
 * @param {HTMLElement} pre
 * @returns {string|null} le slug Prism, ou null si le bloc n'en déclare pas.
 */
function langageDe(pre) {
    const trouve = CLASSE_LANGAGE.exec(pre.className);
    return trouve ? trouve[1] : null;
}

/**
 * Copie le code dans le presse-papier et le signale sur le bouton.
 *
 * L'API Clipboard exige un contexte sécurisé ; en son absence (http local,
 * navigateur ancien) le bouton n'est pas posé du tout, plutôt que de proposer
 * une action qui échouerait.
 *
 * @param {HTMLElement} bouton
 * @param {string} code
 */
async function copier(bouton, code) {
    try {
        await navigator.clipboard.writeText(code);
        bouton.classList.add('is-copie');
        bouton.textContent = 'Copié';
    } catch (e) {
        bouton.classList.add('is-echec');
        bouton.textContent = 'Échec';
    }

    window.setTimeout(() => {
        bouton.classList.remove('is-copie', 'is-echec');
        bouton.textContent = 'Copier';
    }, 2000);
}

/**
 * Construit la barre de titre d'un bloc et l'insère au-dessus du <pre>.
 *
 * @param {HTMLElement} pre
 * @param {string|null} langage
 */
function poserBarre(pre, langage) {
    const conteneur = document.createElement('div');
    conteneur.className = 'stafinity-code';

    const barre = document.createElement('div');
    barre.className = 'stafinity-code_barre';

    const etiquette = document.createElement('span');
    etiquette.className = 'stafinity-code_langage';
    if (langage) {
        etiquette.textContent = ETIQUETTES[langage] || langage.toUpperCase();
    }
    barre.appendChild(etiquette);

    // Pas de contexte sécurisé, pas de bouton : mieux vaut rien qu'un bouton mort.
    if (navigator.clipboard && window.isSecureContext) {
        const bouton = document.createElement('button');
        bouton.type = 'button';
        bouton.className = 'stafinity-code_copier';
        bouton.textContent = 'Copier';
        bouton.setAttribute('aria-label', 'Copier le code');
        bouton.addEventListener('click', () => {
            copier(bouton, pre.textContent);
        });
        barre.appendChild(bouton);
    }

    pre.parentNode.insertBefore(conteneur, pre);
    conteneur.appendChild(barre);
    conteneur.appendChild(pre);
}

function initialiser() {
    const blocs = document.querySelectorAll(SELECTEUR);
    if (!blocs.length) return;

    blocs.forEach((pre) => {
        const langage = langageDe(pre);

        // Prism cherche la classe sur le <code> ou sur un ancêtre : le <pre> la
        // porte déjà, rien à recopier. On marque en revanche le <code> pour que
        // la feuille de style distingue un bloc coloré d'un bloc brut.
        if (langage) {
            pre.classList.add('has-langage');
        }

        poserBarre(pre, langage);
    });

    Prism.highlightAllUnder(document.body);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiser);
} else {
    initialiser();
}
